<template>
  <div class="page-content" style="padding-top: 4rem" v-if="ticketDetails">
    <div class="container-fluid" style="width: 75vw"></div>
    <div class="container">
      <div class="text-center mb-4">
        <h5 class="text-warning fw-bold">
          Ticket - {{ ticketDetails.ticket_id || "..." }}
        </h5>
      </div>

      <div
        id="ticket-content"
        class="card shadow mx-auto"
        style="max-width: 480px"
      >
        <div class="card-body">
          <!-- Header -->
          <div class="mb-5 mt-2 d-flex align-items-center gap-2">
            <img
              src="/images/Frame3839.png"
              class="rounded me-3"
              width="120"
              height="80"
              alt="Bus Image"
            />
            <div>
              <h3 class="fw-bold mb-2">{{ ticketDetails.travelsName }}</h3>
              <small class="fw-bold">{{ ticketDetails.busNo }}</small>
            </div>
          </div>

          <!-- Route -->
          <div class="row mb-3">
            <div class="col">
              <div class="fw-semibold fs-5">{{ ticketDetails.from }}</div>
              <small class="text-muted">Boarding Point</small>
            </div>
            <div class="col text-end">
              <div class="fw-semibold fs-5">{{ ticketDetails.to }}</div>
              <small class="text-muted">Drop Point</small>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col">
              <div class="fw-bold mt-1">
                {{ formatTime(ticketDetails.boardingDateAndTime) }}
              </div>
              <div class="text-muted">
                {{ formatDate(ticketDetails.boardingDateAndTime) }}
              </div>
            </div>
            <div class="col d-flex flex-column justify-content-center">
              <div class="border py-1 text-center" style="border-radius: 100px">
                {{ ticketDetails.totalDuration || "N/A" }} mins
              </div>
            </div>
            <div class="col text-end">
              <div class="fw-bold mt-1">
                {{ formatTime(ticketDetails.dropingDateAndTime) }}
              </div>
              <div class="text-muted">
                {{ formatDate(ticketDetails.dropingDateAndTime) }}
              </div>
            </div>
          </div>

          <!-- Badges -->
          <div class="d-flex justify-content-center gap-2 my-4">
            <span
              class="badge border fw-bold border-danger text-danger d-flex align-items-center gap-1 p-2"
            >
              <i class="ri-check-line"></i> FCFS
            </span>
            <span
              class="badge border text-black d-flex align-items-center gap-1 p-2"
            >
              <i class="ri-map-pin-line"></i>
              {{ ticketDetails.total_km || "N/A" }} kms
            </span>
            <span
              class="badge border text-black d-flex align-items-center gap-1 p-2"
            >
              <i class="ri-star-line"></i>
              {{ ticketDetails.discountedPrice || "N/A" }}
            </span>
          </div>

          <div class="my-dashed-divider border-top mb-3"></div>

          <!-- Fare & Details -->
          <div class="row mb-2 pa-2">
            <div class="d-flex justify-content-between mb-2">
              <p class="text-muted fw-bold">Fare Details</p>
              <p class="text-muted fw-bold">Total</p>
            </div>
            <div class="d-flex justify-content-between mb-2">
              <span class="fw-bold">Adult (x{{ ticketDetails.adult }})</span>
              <span>₹{{ ticketDetails.adultFare }}</span>
            </div>

            <div class="d-flex justify-content-between mb-2">
              <span>Children (x{{ ticketDetails.child }})</span>
              <span>₹{{ ticketDetails.childFare }}</span>
            </div>

            <div class="d-flex justify-content-between mb-2">
              <span>Luggage (x{{ ticketDetails.luggageInUnits }})</span>
              <span>₹{{ ticketDetails.luggageFare }}</span>
            </div>

            <div class="d-flex justify-content-between text-success mb-2">
              <span>Discount</span>
              <span>- ₹{{ ticketDetails.discountedPrice }}</span>
            </div>

            <div class="d-flex justify-content-between text-muted mb-2">
              <span>Route Bus Fee</span>
              <span>₹{{ ticketDetails.routeBusFee }}</span>
            </div>

            <div class="d-flex justify-content-between text-muted mb-2">
              <span>Wallet Points Used</span>
              <span>₹{{ ticketDetails.walletPointsUsed }}</span>
            </div>

            <div class="my-dashed-divider border-top my-2"></div>

            <div class="d-flex justify-content-between fw-bold fs-5">
              <span>Total</span>
              <span>{{ ticketDetails.totalPrice }}</span>
            </div>

            <div class="my-dashed-divider border-top my-2"></div>
          </div>

          <div class="row">
            <div class="col text-start">
              <small class="text-muted">Trip Status</small>
              <div class="fw-bold" style="font-size: 12px">
                First Come First Serve
              </div>
            </div>
            <div class="col text-end">
              <small class="text-muted">Primary Contact</small>
              <div class="fw-bold" style="font-size: 12px">
                {{ ticketDetails.primaryContact }}
              </div>
            </div>
          </div>

          <div class="mt-3">
            <small class="text-muted">Ticket ID</small>
            <div class="fw-bold" style="font-size: 12px">
              {{ ticketDetails.ticket_id }}
            </div>
          </div>

          <!-- Note -->
          <div
            class="alert mt-3 p-2 small alert-message d-flex gap-2"
            role="alert"
          >
            <div>
              <span class="mdi mdi-information fs-4 text-warning"></span>
            </div>
            <div>
              Tickets in this bus are based on
              <strong>First Come First Serve</strong>. If there is no seat, you
              might need to stand.
            </div>
          </div>

          <div class="my-dashed-divider border-top mt-5"></div>

          <!-- QR -->
          <div class="text-center mt-4">
            <QrScanner
              :ticketId="ticketDetails.ticket_id"
              :showBookingID="true"
            />
            <p class="mt-2 small text-muted">
              Show this QR to the Bus Conductor
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="text-center">
      <button class="btn btn-warning me-4 mt-4" @click="downloadPDF">
        Download Ticket
      </button>
      <button
        class="btn text-warning border border-warning mt-4"
        @click="openDeleteDialog()"
      >
        Cancel Ticket
      </button>
    </div>
    <DeleteDialog
      title="Are you Sure?"
      message="Are you sure you want to cancel this Ticket?"
      :cancel-text="'No'"
      :confirm-text="'Yes'"
      ref="deleteDialog"
      @confirm="cancelTicket"
    />
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import html2pdf from "html2pdf.js/dist/html2pdf.bundle.min";
import QrScanner from "@/components/qr/QrScanner.vue";
import { cancelTicketById } from "@/apis/booking";
import DeleteDialog from "@/components/dialog/DeleteDialog.vue";
import ToastNotification from "@/components/dialog/ToastNotification.vue";
export default {
  data() {
    return {
      ticketDetails: null,
    };
  },
  components: {
    QrScanner,
    DeleteDialog,
    ToastNotification,
  },
  methods: {
    downloadPDF() {
      this.$nextTick(() => {
        const qrCanvas = document.querySelector("#ticket-content canvas");
        if (qrCanvas) {
          const img = document.createElement("img");
          img.src = qrCanvas.toDataURL("image/png");
          img.style.width = "150px";
          qrCanvas.replaceWith(img);
        }

        const element = document.getElementById("ticket-content");

        html2pdf()
          .from(element)
          .set({
            margin: [5, 5, 5, 5], // very small margin → fit in single page
            filename: `bus_ticket_${
              this.ticketDetails?.ticket_id || "ticket"
            }.pdf`,
            html2canvas: { scale: 3, scrollY: 0 }, // fixes crop + blank areas
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          })
          .save();
      });
    },

    openDeleteDialog(user) {
      this.$refs.deleteDialog.openDialog(user);
    },

    async cancelTicket() {
      const response = await cancelTicketById(this.ticketDetails.ticket_id);
      if (response.success) {
        this.$refs.toast.showToast(`Ticket Cancelled Successfully`, "success");
        setTimeout(() => {
          this.$router.push("/bookings");
        }, 2000);
      } else {
        this.$refs.toast.showToast(`Failed to Cancel ticket`, "error");
      }
    },

    formatTime(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDate(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toDateString();
    },
  },
  mounted() {
    const ticketData = localStorage.getItem("ticket_data");
    if (ticketData) {
      this.ticketDetails = JSON.parse(ticketData);
    }
  },
};
</script>

<style scoped>
.page-content {
  background-color: #f9f9f9;
}

.my-dashed-divider {
  border-top-style: dashed !important;
  border-top-color: hsl(210, 14%, 89%);
  border-top-width: 2px;
}

.alert-message {
  background-color: #f8ffc1;
}

#ticket-content {
  max-height: 270mm;
  overflow-y: scroll;
}

#ticket-content * {
  page-break-inside: avoid !important;
}
</style>
